<template>
  <div class="record-detail-container">
    <el-card class="main-card">
      <!-- 按钮组 -->
      <div class="button-group">
        <el-button
          @click="downloadAllFiles"
          type="primary"
          :loading="downloadLoading"
          class="action-button download-button"
        >
          <template v-if="!downloadLoading">
            <el-icon class="button-icon"><Download /></el-icon>
          </template>
          <span class="button-text">Download All Files</span>
          <span class="button-text-short">Download</span>
        </el-button>
        <el-button
          @click="goBack"
          type="primary"
          class="action-button back-button"
        >
          <el-icon class="button-icon"><Back /></el-icon>
          <span class="button-text">Back to Operation History</span>
          <span class="button-text-short">Back</span>
        </el-button>
      </div>

      <!-- 加载状态 -->
      <div v-if="loading" class="loading-container">
        <el-loading-spinner size="large" />
        <p>Loading session data...</p>
      </div>

      <!-- 数据展示 -->
      <div v-else-if="sessionData">
        <!-- 无有效文件时显示提示 -->
        <div v-if="!hasValidFiles" class="no-files-container">
          <el-empty description="No Generated Files Because the Analysis is Not Completed" :image-size="120" />
        </div>
        <el-tabs v-else v-model="activeTab" type="border-card">
          <!-- Upload Data Tab -->
          <el-tab-pane
            v-if="sessionData.upload_data"
            label="Upload Data"
            name="upload_data"
          >
            <template #label>
              <span class="tab-label">
                <el-icon><Upload /></el-icon>
                <span class="tab-text">Upload Data</span>
                <span class="tab-text-short">Upload</span>
              </span>
            </template>
            <div class="table-wrapper">
              <el-table
                :data="parsedUploadData"
                :columns="generateColumns(sessionData.upload_data.columns)"
                stripe
                border
                :height="tableHeight"
                style="width: 100%"
                :scrollbar-always-on="false"
                :flexible="true"
              >
                <el-table-column
                  v-for="col in sessionData.upload_data.columns"
                  :key="col"
                  :prop="col"
                  :label="col"
                  show-overflow-tooltip
                  :min-width="120"
                />
              </el-table>
            </div>
            <div class="table-footer">
              <el-tag type="info" size="small">
                {{ sessionData.upload_data.row_count }} rows
              </el-tag>
            </div>
          </el-tab-pane>

          <!-- Tidy Data Tab -->
          <el-tab-pane
            v-if="sessionData.tidy_data"
            label="Tidy Data"
            name="tidy_data"
          >
            <template #label>
              <span class="tab-label">
                <el-icon><Document /></el-icon>
                <span class="tab-text">Tidy Data</span>
                <span class="tab-text-short">Tidy</span>
              </span>
            </template>
            <div class="table-wrapper">
              <el-table
                :data="parsedTidyData"
                :columns="generateColumns(sessionData.tidy_data.columns)"
                stripe
                border
                :height="tableHeight"
                style="width: 100%"
                :scrollbar-always-on="false"
                :flexible="true"
              >
                <el-table-column
                  v-for="col in sessionData.tidy_data.columns"
                  :key="col"
                  :prop="col"
                  :label="col"
                  show-overflow-tooltip
                  :min-width="120"
                />
              </el-table>
            </div>
            <div class="table-footer">
              <el-tag type="success" size="small">
                {{ sessionData.tidy_data.row_count }} rows
              </el-tag>
            </div>
          </el-tab-pane>

          <!-- Build Model Tabs -->
          <el-tab-pane
            v-for="(fileData, filename) in sessionData.buildmodel_data"
            :key="filename"
            :label="filename"
            :name="`buildmodel_${filename}`"
          >
            <template #label>
              <span class="tab-label">
                <el-icon><TrendCharts /></el-icon>
                <span class="tab-text">{{ filename }}</span>
                <span class="tab-text-short">{{ shortenFilename(filename) }}</span>
              </span>
            </template>
            <div class="table-wrapper">
              <el-table
                :data="getParsedBuildModelData(filename)"
                :columns="generateColumns(fileData.columns)"
                stripe
                border
                :height="tableHeight"
                style="width: 100%"
                :scrollbar-always-on="false"
                :flexible="true"
              >
                <el-table-column
                  v-for="col in fileData.columns"
                  :key="col"
                  :prop="col"
                  :label="col"
                  show-overflow-tooltip
                  :min-width="120"
                />
              </el-table>
            </div>
            <div class="table-footer">
              <el-tag type="warning" size="small">
                {{ fileData.row_count }} rows
              </el-tag>
            </div>
          </el-tab-pane>
        </el-tabs>
      </div>

      <!-- 错误状态 -->
      <div v-else-if="error" class="error-container">
        <el-alert
          :title="error"
          type="error"
          :closable="false"
          show-icon
        />
      </div>
    </el-card>

    <!-- 下载文件名弹窗 -->
    <el-dialog
      v-model="downloadDialogVisible"
      title="Download Files"
      width="400px"
      :close-on-click-modal="false"
    >
      <el-form>
        <el-form-item label="File Name">
          <el-input
            v-model="downloadFileName"
            placeholder="Enter file name"
            suffix-icon="el-icon-document"
          >
            <template #append>.zip</template>
          </el-input>
        </el-form-item>
      </el-form>
      <template #footer>
        <el-button @click="downloadDialogVisible = false">Cancel</el-button>
        <el-button type="primary" @click="confirmDownload" :loading="downloadLoading">
          Download
        </el-button>
      </template>
    </el-dialog>
  </div>
</template>

<script>
import axios from 'axios';
import { ElMessage } from 'element-plus';
import { Upload, Document, TrendCharts, Download, Back } from '@element-plus/icons-vue';
import Papa from 'papaparse';

export default {
  name: 'RecordDetail',
  components: {
    Upload,
    Document,
    TrendCharts,
    Download,
    Back
  },
  data() {
    return {
      activeTab: 'upload_data',
      sessionId: null,
      moduleName: null,
      sessionData: null,
      loading: false,
      error: null,
      downloadLoading: false,
      // 下载弹窗
      downloadDialogVisible: false,
      downloadFileName: '',
      // 解析后的数据
      parsedUploadData: [],
      parsedTidyData: [],
      parsedBuildModelData: {},
      // 响应式表格高度
      tableHeight: 400,
    };
  },
  computed: {
    isMobile() {
      return window.innerWidth < 600;
    },
    hasValidFiles() {
      if (!this.sessionData) return false;
      const hasUpload = this.sessionData.upload_data && this.sessionData.upload_data.csv_content;
      const hasTidy = this.sessionData.tidy_data && this.sessionData.tidy_data.csv_content;
      const hasBuildModel = this.sessionData.buildmodel_data && Object.keys(this.sessionData.buildmodel_data).length > 0;
      return hasUpload || hasTidy || hasBuildModel;
    }
  },
  mounted() {
    this.$store.dispatch('fetchLoginStatus');  // 调用 Vuex 的登录状态检查
    this.fetchRecordDetails();  // 加载数据
    this.updateTableHeight();

    // 使用防抖处理 resize 事件
    this.resizeHandler = this.debounce(this.updateTableHeight, 200);
    window.addEventListener('resize', this.resizeHandler);
  },
  beforeUnmount() {
    // 清理 resize 事件监听器
    if (this.resizeHandler) {
      window.removeEventListener('resize', this.resizeHandler);
    }
  },
  methods: {
    // 防抖函数
    debounce(func, wait) {
      let timeout;
      return function executedFunction(...args) {
        const later = () => {
          clearTimeout(timeout);
          func(...args);
        };
        clearTimeout(timeout);
        timeout = setTimeout(later, wait);
      };
    },

    // 更新表格高度（响应式）
    updateTableHeight() {
      const isMobile = window.innerWidth < 600;
      this.tableHeight = isMobile ? 300 : 400;
    },

    // 缩短文件名（用于移动端显示）
    shortenFilename(filename) {
      if (!filename) return '';
      // 如果文件名太长，截取前10个字符
      if (filename.length > 12) {
        return filename.substring(0, 10) + '...';
      }
      return filename;
    },
    async fetchRecordDetails() {
      try {
        this.loading = true;
        this.error = null;
        
        const urlParams = new URLSearchParams(window.location.search);
        this.sessionId = urlParams.get('session');
        if (!this.sessionId) {
          this.error = 'Session ID is missing.';
          return;
        }
        const isExampleParam = urlParams.get('isExample');
        const moduleParam = urlParams.get('module');
        this.moduleName = moduleParam;
        const isExample = isExampleParam === 'true';

        console.log('Fetching session data for:', this.sessionId);
        
        // 使用新的API接口获取所有文件详细数据
        const response = await axios.post('/api/session/allFileDetailData/', {
          session: this.sessionId,
          ...(isExample ? { isExample: true, module: moduleParam || '' } : {})
        });
        
        console.log('API Response:', response.data);
        
        if (response.data.success) {
          this.sessionData = response.data.data;
          console.log('Session data loaded:', this.sessionData);
          
          // 解析CSV数据
          this.parseAllCsvData();
          
          // 设置默认选中的tab
          if (this.sessionData.upload_data) {
            this.activeTab = 'upload_data';
          } else if (this.sessionData.tidy_data) {
            this.activeTab = 'tidy_data';
          } else if (Object.keys(this.sessionData.buildmodel_data || {}).length > 0) {
            const firstBuildModelFile = Object.keys(this.sessionData.buildmodel_data)[0];
            this.activeTab = `buildmodel_${firstBuildModelFile}`;
          }
        } else {
          this.error = response.data.message || 'Failed to load session data';
        }
      } catch (error) {
        console.error('Error fetching session details:', error);
        this.error = error.response?.data?.message || error.message || 'Failed to load session data';
      } finally {
        this.loading = false;
      }
    },

    // 解析所有CSV数据
    parseAllCsvData() {
      // 解析上传数据
      if (this.sessionData.upload_data && this.sessionData.upload_data.csv_content) {
        this.parsedUploadData = this.parseCsvContent(this.sessionData.upload_data.csv_content);
      }
      
      // 解析整理数据
      if (this.sessionData.tidy_data && this.sessionData.tidy_data.csv_content) {
        this.parsedTidyData = this.parseCsvContent(this.sessionData.tidy_data.csv_content);
      }
      
      // 解析构建模型数据
      if (this.sessionData.buildmodel_data) {
        this.parsedBuildModelData = {};
        Object.keys(this.sessionData.buildmodel_data).forEach(filename => {
          const fileData = this.sessionData.buildmodel_data[filename];
          if (fileData.csv_content) {
            this.parsedBuildModelData[filename] = this.parseCsvContent(fileData.csv_content);
          }
        });
      }
    },

    // 解析CSV内容 - 使用Papa Parse库正确处理CSV格式
    parseCsvContent(csvContent) {
      if (!csvContent) return [];

      try {
        // 使用Papa Parse解析CSV,它可以正确处理引号、逗号等特殊情况
        const result = Papa.parse(csvContent, {
          header: true,           // 第一行作为header
          skipEmptyLines: true,   // 跳过空行
          dynamicTyping: false,   // 不自动转换类型,保持字符串
          trimHeaders: true,      // 去除header的空格
          transform: (value) => {
            // 去除字段前后的空格和引号
            if (typeof value === 'string') {
              return value.trim().replace(/^["']|["']$/g, '');
            }
            return value;
          }
        });

        if (result.errors && result.errors.length > 0) {
          console.warn('CSV parsing warnings:', result.errors);
        }

        return result.data || [];
      } catch (error) {
        console.error('Error parsing CSV content:', error);
        return [];
      }
    },

    // 获取解析后的构建模型数据
    getParsedBuildModelData(filename) {
      return this.parsedBuildModelData[filename] || [];
    },

    // 生成表格列配置
    generateColumns(columns) {
      if (!columns || !Array.isArray(columns)) {
        return [];
      }
      
      return columns.map(column => ({
        prop: column,
        label: column,
        width: 'auto',
        showOverflowTooltip: true,
      }));
    },

    // 打开下载弹窗
    downloadAllFiles() {
      if (!this.sessionId) {
        this.showSnackbar('Session ID is missing.', 'error');
        return;
      }

      if (!this.moduleName) {
        this.showSnackbar('Module name is missing.', 'error');
        return;
      }

      // 设置默认文件名并打开弹窗
      this.downloadFileName = `${this.moduleName}_summary_${this.sessionId}`;
      this.downloadDialogVisible = true;
    },

    // 确认下载
    async confirmDownload() {
      if (!this.downloadFileName.trim()) {
        this.showSnackbar('Please enter a file name.', 'error');
        return;
      }

      this.downloadLoading = true;
      console.log('开始下载所有文件，session:', this.sessionId, 'module:', this.moduleName);

      try {
        const downloadUrl = `/api/session/Summary/download/${this.sessionId}/${this.moduleName}/`;

        const response = await axios.post(downloadUrl, {}, {
          responseType: 'blob'
        });

        // 创建Blob对象
        const blob = new Blob([response.data], { type: 'application/zip' });
        const url = window.URL.createObjectURL(blob);

        // 创建下载链接，使用用户输入的文件名
        const link = document.createElement('a');
        link.href = url;
        link.download = `${this.downloadFileName.trim()}.zip`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        // 清理URL对象
        window.URL.revokeObjectURL(url);

        this.downloadDialogVisible = false;
        this.showSnackbar('Download completed.', 'success');
      } catch (error) {
        console.error('下载文件失败:', error);
        this.showSnackbar('Download failed: ' + (error.message || 'Unknown error'), 'error');
      } finally {
        this.downloadLoading = false;
      }
    },

    goBack() {
      this.$router.push('/dashboard');
    },

    showSnackbar(message, type = 'info') {
      ElMessage({
        message,
        type,
        duration: 3000
      });
    },
  },
};
</script>

<style scoped>
.record-detail-container {
  padding: 20px;
  max-width: 1200px;
  margin: 0 auto;
}

.main-card {
  margin-bottom: 20px;
}

.button-group {
  display: flex;
  gap: 16px;
  margin: 0 0 20px 0;
  justify-content: flex-end;
  align-items: stretch;
}

/* 按钮基础样式 */
.action-button {
  min-width: 10rem;
  margin: 0 !important;
}

/* 图标样式 */
.button-icon,
.action-button :deep(.el-icon.is-loading) {
  margin-right: 6px;
  width: 16px;
  height: 16px;
}

.button-icon :deep(svg),
.action-button :deep(.el-icon.is-loading svg) {
  display: block;
}

/* 桌面端按钮内边距 */
.download-button,
.back-button {
  padding: 0 20px !important;
}

.button-text-short {
  display: none;
}

.loading-container {
  text-align: center;
  padding: 40px;
}

.loading-container p {
  margin-top: 16px;
  color: #666;
}

.error-container {
  padding: 20px;
  max-width: 500px;
  margin: 0 auto;
}

.no-files-container {
  padding: 60px 20px;
  text-align: center;
}

.table-footer {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  margin-top: 12px;
  padding-right: 8px;
}

/* 表格容器 */
.table-wrapper {
  overflow-x: auto;
  width: 100%;
}

/* 标签文本 */
.tab-label {
  display: flex;
  align-items: center;
  gap: 4px;
}

.tab-text-short {
  display: none;
}

/* Element Plus 样式优化 */
:deep(.el-table) {
  border-radius: 8px;
}

:deep(.el-table th) {
  background-color: #f5f7fa;
  font-weight: 600;
}

:deep(.el-tabs__item .el-icon) {
  margin-right: 4px;
}

/* ========== 平板端优化 (600px - 960px) ========== */
@media (max-width: 960px) {
  .record-detail-container {
    padding: 16px;
  }

  .button-group {
    gap: 12px;
  }

  .action-button {
    min-width: 8rem;
    font-size: 14px;
  }
}

/* ========== 移动端优化 (<600px) ========== */
@media (max-width: 600px) {
  .record-detail-container {
    padding: 8px;
  }

  .main-card {
    margin-bottom: 12px;
  }

  :deep(.el-card__body) {
    padding: 12px;
  }

  /* 按钮组水平布局 */
  .button-group {
    flex-direction: row;
    gap: 8px;
    margin-bottom: 16px;
    justify-content: space-between;
  }

  .action-button {
    flex: 1;
    min-width: 0;
    height: 44px;
    font-size: 14px;
  }

  .button-icon,
  .action-button :deep(.el-icon.is-loading) {
    width: 18px;
    height: 18px;
  }

  .button-text {
    display: none;
  }

  .button-text-short {
    display: inline;
  }

  .download-button,
  .back-button {
    padding: 0 12px !important;
  }

  /* 标签页优化 */
  :deep(.el-tabs__nav-scroll) {
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
  }

  :deep(.el-tabs__item) {
    padding: 0 12px;
    font-size: 13px;
  }

  /* 标签文本简化 */
  .tab-text {
    display: none;
  }

  .tab-text-short {
    display: inline;
  }

  /* 表格优化 */
  .table-wrapper {
    margin: 0 -12px;
    width: calc(100% + 24px);
  }

  :deep(.el-table) {
    font-size: 12px;
    border-radius: 0;
  }

  :deep(.el-table th),
  :deep(.el-table td) {
    padding: 8px 6px;
    font-size: 12px;
  }

  :deep(.el-table .cell) {
    line-height: 1.4;
  }

  .table-footer {
    padding: 8px 12px;
  }

  .loading-container {
    padding: 30px 20px;
  }

  .error-container {
    padding: 16px;
  }
}

/* ========== 小屏手机优化 (<400px) ========== */
@media (max-width: 400px) {
  .record-detail-container {
    padding: 4px;
  }

  :deep(.el-card__body) {
    padding: 8px;
  }

  .action-button {
    height: 40px;
    font-size: 13px;
  }

  .download-button,
  .back-button {
    padding: 0 8px !important;
  }

  :deep(.el-tabs__item) {
    font-size: 12px;
  }

  :deep(.el-table) {
    font-size: 11px;
  }

  :deep(.el-table th),
  :deep(.el-table td) {
    padding: 6px 4px;
  }

  .table-footer {
    padding: 6px 8px;
  }
}
</style>
